import React from 'react'

import { graphql, useStaticQuery } from 'gatsby'

import Image from '@components/Content/Image/Image'
import SocialIcons from '@components/Content/SocialIcons/SocialIcons'
import Wysiwyg from '@components/Content/Wysiwyg/Wysiwyg'
import FooterCompanyMenu from '@components/Footer/FooterCompanyMenu'
import FooterServicesMenu from '@components/Footer/FooterServicesMenu'
import Container from '@components/Layout/Container'
import SectionTitle from '@components/Section/SectionTitle'

import { FooterDataType } from '@contracts/Footer'

import * as styles from './Footer.module.scss'

import FooterLogo from '@images/logo-minimal.svg'

interface Props {
	path: string | null
}

const Footer = (props: Props) => {
	const data = useStaticQuery<FooterDataType>(graphql`
		query Footer {
			wp {
				themeSettings {
					themeSettingsOptions {
						address
						officeNumber
						supportNumber
						email
					}
				}
				acfOptionsFooterSettings {
					footerSettingsOptions {
						accreditationsImage {
							altText
							mimeType
							localFile {
								publicURL
								childImageSharp {
									gatsbyImageData(width: 800)
								}
							}
						}
					}
				}
			}
		}
	`)

	const scrollToTop = () => {
		window.scrollTo({ top: 0, behavior: 'smooth' })
	}

	const { address, officeNumber, supportNumber, email } = data.wp.themeSettings.themeSettingsOptions
	const { accreditationsImage } = data.wp.acfOptionsFooterSettings.footerSettingsOptions

	// Sanitize the office and support numbers
	const officeNumberSanitized = officeNumber.replace(/[^0-9]/g, '')
	const supportNumberSanitized = supportNumber.replace(/[^0-9]/g, '')

	return (
		<footer className={styles.footer}>
			<Container className={styles.container} type="wide">
				<div className={styles.footerSection}>
					<FooterLogo />

					<Wysiwyg content={address} className={styles.address} />

					<div className={styles.contactDetails}>
						<div className={styles.contactDetail}>
							<span>Office: </span>
							<a href={`tel:+${officeNumberSanitized}`}>{officeNumber}</a>
						</div>
						<div className={styles.contactDetail}>
							<span>24/7 Client Support: </span>
							<a href={`tel:+${supportNumberSanitized}`}>{supportNumber}</a>
						</div>
						<div className={styles.contactDetail}>
							<span>Email: </span>
							<a href={`mail:${email}`}>{email}</a>
						</div>
					</div>

					<SocialIcons />
				</div>

				<div className={styles.footerSection}>
					<h3 className={styles.title}>Services</h3>
					<FooterServicesMenu />
				</div>

				<div className={styles.footerSection}>
					<h3 className={styles.title}>Company</h3>
					<FooterCompanyMenu />
				</div>

				<div className={styles.footerSection}>
					<h3 className={styles.title}>Awards & Accreditations</h3>

					<Image image={accreditationsImage} altText="Awards & Accreditations" />

					<div className={styles.scrollToTopContainer}>
						<SectionTitle
							title={[
								{
									styledText: {
										text: 'Security',
										isSplit: false,
										isThin: false,
										isYellow: false,
									},
								},
								{
									styledText: {
										text: 'Made Simple',
										isSplit: true,
										isThin: false,
										isYellow: true,
									},
								},
							]}
							className={styles.taglineTitle}
						/>

						<div className={styles.scrollToTop} onClick={scrollToTop}></div>
					</div>

					<span className={styles.copyright}>
						<small>
							&copy; {new Date().getFullYear()} KryptoKloud Limited. Design by{' '}
							<a href="https://warboxcreative.co.uk/" rel={props.path == '/' ? 'follow' : 'nofollow'}>
								Warbox
							</a>
						</small>
					</span>
				</div>
			</Container>
		</footer>
	)
}

export default Footer
